'use client';
import { useEffect, useRef, useState } from 'react';
import { motion, useScroll } from 'framer-motion';

import ContentClientEnhancer from './ContentClientEnhancer';

interface ArticleSectionClientProps {
  content: string;
  headings: Array<{ id: string; text: string }>;
  faqHeading: string;
  faqArr: {
    heading: string;
    description: string;
  }[];
}

const ArticleSectionClient = ({
  content,
  headings,
  faqHeading,
  faqArr,
}: ArticleSectionClientProps) => {
  const [selectedSection, setSelectedSection] = useState('');
  const [scrolling, setScrolling] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const element = useRef(null);
  const { scrollYProgress } = useScroll({
    target: element,
  });

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const topOffset =
        element.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: topOffset, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = window.scrollY;

      if (currentPosition > scrollTop + 3) {
        setScrolling(true);
      } else if (currentPosition < scrollTop - 3) {
        setScrolling(false);
      }
      setScrollTop(currentPosition);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollTop]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollTop = window.scrollY + 100;

      for (let i = headings.length; i >= 1; i--) {
        const headingElement = document.getElementById(headings[i - 1].id);
        if (headingElement) {
          const topOffset =
            headingElement.getBoundingClientRect().top +
            window.pageYOffset -
            100;
          if (currentScrollTop >= topOffset) {
            setSelectedSection(headings[i - 1].id);
            return;
          }
        }
      }

      const faqElement = document.getElementById('blogFaq');
      if (faqElement) {
        const faqOffset = faqElement.offsetTop;
        if (currentScrollTop >= faqOffset) {
          setSelectedSection('blogFaq');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  useEffect(() => {
    setTimeout(() => {
      if (window.location.hash) {
        setSelectedSection(window.location.hash.substring(1));
      }
    }, 200);
  }, [headings]);

  useEffect(() => {
    const instagramScript = document.createElement('script');
    instagramScript.src = 'https://www.instagram.com/embed.js';
    instagramScript.async = true;

    const tiktokScript = document.createElement('script');
    tiktokScript.src = 'https://www.tiktok.com/embed.js';
    tiktokScript.async = true;

    document.body.appendChild(instagramScript);
    document.body.appendChild(tiktokScript);

    return () => {
      document.body.removeChild(instagramScript);
      document.body.removeChild(tiktokScript);
    };
  }, []);

  return (
    <>
      <div ref={element} className="w-max flex-col lg:flex-row flex gap-2">
        <motion.div
          className="fixed left-0 origin-left w-full top-0 z-50 h-[6px] bg-black"
          style={{ scaleX: scrollYProgress }}
        />

        <div className="md:w-[200px] w-full flex flex-col md:py-5 py-0 md:px-0 md:border-none md:border-y-2 border-none border-black/30">
          <div
            className={`md:sticky relative w-full ${scrolling ? 'md:top-5' : 'md:top-20'} transition-all duration-300`}
          >
            <h2 className="font-semibold md:text-[18px] text-[14px] font-proxima_font">
              In This Article
            </h2>
            <ul className="mt-2">
              {headings.map((heading) => (
                <li
                  key={heading.id}
                  className="flex relative gap-2 mt-[0.5px] items-center"
                >
                  <a
                    href={`#${heading.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedSection(heading.id);
                      scrollToSection(heading.id);
                    }}
                    className={`${selectedSection === heading.id ? 'text-black' : 'text-[#666D80]'} font-semibold cursor-pointer text-[14px] font-proxima_font gap-2 my-1 px-2 block md:w-[200px] w-full whitespace-normal break-words`}
                  >
                    {heading.text}
                  </a>
                </li>
              ))}
              {faqHeading && faqArr && (
                <li className="flex relative gap-2 mt-[0.5px] items-center">
                  <a
                    href="#blogFaq"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedSection('blogFaq');
                      scrollToSection('blogFaq');
                    }}
                    className={`${selectedSection === 'blogFaq' ? 'text-black' : 'text-[#666D80]'} font-semibold cursor-pointer text-[14px] font-proxima_font text-wrap flex gap-2 my-1 px-2`}
                  >
                    FAQs
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
      <ContentClientEnhancer content={content} />
    </>
  );
};

export default ArticleSectionClient;
